import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import backendUrl from '../config';
import { parseMessageSources as parseMessageSourcesUtil } from '../../utils/messageUtils';
import { mergePersistedSteps } from '../../utils/mergePersistedSteps';
// import { StreamingFormatter } from '../../utils/streamingFormatter'; // Replaced by unified backend processor
import { generateUUID, isValidUUID } from '../../utils/uuid';
import { logout } from './authSlice';

// Module-level abort controller so the UI can cancel in-flight streaming requests
let activeAbortController: AbortController | null = null;

export const abortStreamingRequest = () => {
  if (activeAbortController) {
    activeAbortController.abort();
    activeAbortController = null;
  }
};

export interface EmailSource {
  subject: string;
  from: string;
  date: string;
  file?: string;
  score?: number;
  url?: string;
  body?: string;
}

export interface WebSearch {
  query: string;
  results?: string;
  urls?: string[];
  preview?: string; // Keep for backward compatibility
}

export interface ArticleSource {
  title: string;
  url: string;
  domain?: string;
  article_date?: string;
  score?: number;
  source_email_subjects?: string[];
  source_email_dates?: string[];
}

export interface CompanySource {
  name?: string;
  title?: string;
  url?: string;
  domain?: string;
  description?: string;
  score?: number;
}

// Entry in `metadata.sources.combined` — the deduped union the
// backend builds across all source kinds it wants surfaced. Each item
// carries a `type` discriminator ("web", "article", "email",
// "company", "url" …) so the renderer can route it.
export interface CombinedSource {
  url?: string;
  title?: string;
  type?: string;
  domain?: string;
  source?: string;
  // Email/company-shaped entries occasionally show up too — keep open.
  [key: string]: unknown;
}

export interface MessageMetadata {
  tools_used?: string[];
  // Legacy flat structure (for backward compatibility)
  article_sources?: ArticleSource[];
  company_sources?: CompanySource[];
  email_sources?: EmailSource[];
  web_searches?: WebSearch[];
  urls_visited?: string[];
  // New nested structure from anis_chatbot API
  sources?: {
    articles?: ArticleSource[] | number;
    companies?: CompanySource[] | number;
    emails?: EmailSource[] | number;
    web_searches?: WebSearch[];
    urls_visited?: string[];
    // Deduped union the upstream backend builds from all source kinds —
    // populated for many real queries even when articles/web_searches
    // are empty. messageMapper falls back to this when nothing else
    // produced source chips.
    combined?: CombinedSource[];
    searches?: number; // Keep for backward compatibility
    urls?: number; // Keep for backward compatibility
  };
  // Intermediate step timeline — see CHAT_INTERMEDIATE_STEPS_SPEC.
  // Captured during streaming and/or restored from the server on message
  // reload. Rendered by <MessageSteps /> above the assistant bubble.
  steps?: StepRecord[];
  // Structured follow-up suggestions emitted by the backend (shipped
  // 2026-05-11 alongside ?mode=fast|expert). Each entry is a ready-to-
  // send prompt — chat-v2 surfaces them as clickable chips below the
  // assistant bubble (assistant-ui ThreadSuggestion standard).
  follow_up_questions?: string[];
}

export type StepKind = 'tool_call' | 'retrieval' | 'web_search' | 'thinking' | string;
export type StepStatus = 'running' | 'ok' | 'error';

export interface StepRecord {
  id: string;
  kind: StepKind;
  label: string;
  detail?: string | null;
  status: StepStatus;
  started_at: string;              // ISO-8601 UTC
  ended_at?: string | null;        // populated when status transitions off "running"
  duration_ms?: number | null;
  result_preview?: string | null;
  error_message?: string | null;
}

export interface ChatMessage {
  id: string;
  // BE-assigned canonical ID, populated on the SSE `complete` event.
  // Kept SEPARATE from `id` so assistant-ui's external-store runtime
  // sees a stable thread-message identity across the temp→real swap;
  // mutating `id` mid-stream forks the message in MessageRepository
  // (creates a sibling branch), orphaning the post-stream rating
  // data part on the old branch.
  serverMessageId?: string;
  session_id: string;
  sender: 'user' | 'bot';
  content: string;
  sources?: string[];
  metadata?: MessageMetadata;
  created_at: string;
  sequence_number?: number;
}
export interface ChatSession {
  id: string;
  title?: string | null;
  created_at: string;
  messages: ChatMessage[];
  last_message_at?: string | null;
  last_message_preview?: string | null;
  messagesLoaded?: boolean; // client-side: true once full messages have been fetched
}

export interface Rating {
  id: string;
  message_id: string;
  user_id: string;
  rating_type: 'thumbs' | 'stars';
  rating_value: number; // -1/1 for thumbs, 1-5 for stars
  feedback_text?: string;
  created_at: string;
}

export type ChatMode = 'fast' | 'expert';

interface ChatState {
  sessions: ChatSession[];
  currentSession: ChatSession | null;
  loading: boolean;
  error: string | null;
  messageRatings: { [messageId: string]: Rating }; // Track ratings by message ID
  selectedSource: string; // Knowledge base source: starch, non_food, sns
  chatMode: ChatMode;
}

const loadPersistedChatMode = (): ChatMode => {
  try {
    return localStorage.getItem('aihub_chat_mode') === 'fast' ? 'fast' : 'expert';
  } catch {
    return 'expert';
  }
};

const initialState: ChatState = {
  sessions: [],
  currentSession: null,
  loading: false,
  error: null,
  messageRatings: {},
  selectedSource: 'starch',
  chatMode: loadPersistedChatMode(),
};

// Helper functions for session persistence
const saveCurrentSessionId = (sessionId: string | null) => {
  if (sessionId) {
    localStorage.setItem('currentChatSessionId', sessionId);
  } else {
    localStorage.removeItem('currentChatSessionId');
  }
};

const handleUnauthorized = (dispatch: any) => {
  try {
    sessionStorage.setItem(
      'sessionExpiredMessage',
      'Your session is no longer valid. Please log in again to continue.'
    );
  } catch {}

  try {
    dispatch(logout());
  } catch {}

  // Redirect to login with a slight delay to ensure state is updated
  setTimeout(() => {
    window.location.href = '/login';
  }, 100);
};

// Mirror backend sequencing logic for optimistic UI messages.
// Backend uses odd sequence numbers for user and even for bot to enforce ordering.
const getNextSequenceNumber = (messages: ChatMessage[] | undefined, sender: 'user' | 'bot'): number => {
  const maxSeq = (messages || []).reduce((max, m) => {
    const seq = typeof m.sequence_number === 'number' ? m.sequence_number : undefined;
    return typeof seq === 'number' && seq > max ? seq : max;
  }, 0);

  if (maxSeq === 0) {
    return sender === 'user' ? 1 : 2;
  }

  if (sender === 'user') {
    return maxSeq % 2 === 0 ? maxSeq + 1 : maxSeq + 2;
  }
  return maxSeq % 2 === 1 ? maxSeq + 1 : maxSeq + 2;
};

export const fetchSessions = createAsyncThunk('chat/fetchSessions', async (_, { getState, dispatch, rejectWithValue }) => {
  try {
    const { auth } = getState() as { auth: { token: string } };
    const res = await axios.get(`${backendUrl}/api/chat/sessions`, { headers: { Authorization: `Bearer ${auth.token}` } });
    return res.data as ChatSession[];
  } catch (e: any) {
    if (e?.response?.status === 401) {
      // Cannot use global axios interceptor here (chatSlice uses raw axios),
      // so handle unauthorized consistently.
      handleUnauthorized(dispatch);
      return rejectWithValue('Could not validate credentials');
    }
    return rejectWithValue(e.response?.data?.detail || 'Failed to fetch sessions');
  }
});

export const createSession = createAsyncThunk('chat/createSession', async (title: string | undefined, { getState, dispatch, rejectWithValue }) => {
  try {
    const { auth } = getState() as { auth: { token: string } };
    const res = await axios.post(`${backendUrl}/api/chat/sessions`, { title }, { headers: { Authorization: `Bearer ${auth.token}` } });
    return res.data as ChatSession;
  } catch (e: any) {
    if (e?.response?.status === 401) {
      handleUnauthorized(dispatch);
      return rejectWithValue('Could not validate credentials');
    }
    return rejectWithValue(e.response?.data?.detail || 'Failed to create session');
  }
});

export const sendMessage = createAsyncThunk('chat/sendMessage', async ({ sessionId, sender, content }: { sessionId: string; sender: 'user' | 'bot'; content: string }, { getState, dispatch, rejectWithValue }) => {
  try {
    const { auth } = getState() as { auth: { token: string } };
    const res = await axios.post(`${backendUrl}/api/chat/sessions/${sessionId}/messages`, { sender, content }, { headers: { Authorization: `Bearer ${auth.token}` } });
    return res.data as ChatMessage;
  } catch (e: any) {
    if (e?.response?.status === 401) {
      handleUnauthorized(dispatch);
      return rejectWithValue('Could not validate credentials');
    }
    return rejectWithValue(e.response?.data?.detail || 'Failed to send message');
  }
});

// Session management actions
export const updateSession = createAsyncThunk('chat/updateSession', async ({ sessionId, title }: { sessionId: string; title: string }, { getState, dispatch, rejectWithValue }) => {
  try {
    const { auth } = getState() as { auth: { token: string } };
    const res = await axios.put(`${backendUrl}/api/chat/sessions/${sessionId}`, { title }, { headers: { Authorization: `Bearer ${auth.token}` } });
    return res.data as ChatSession;
  } catch (e: any) {
    if (e?.response?.status === 401) {
      handleUnauthorized(dispatch);
      return rejectWithValue('Could not validate credentials');
    }
    return rejectWithValue(e.response?.data?.detail || 'Failed to update session');
  }
});

export const deleteSession = createAsyncThunk('chat/deleteSession', async (sessionId: string, { getState, dispatch, rejectWithValue }) => {
  try {
    const { auth } = getState() as { auth: { token: string } };
    await axios.delete(`${backendUrl}/api/chat/sessions/${sessionId}`, { headers: { Authorization: `Bearer ${auth.token}` } });
    return sessionId;
  } catch (e: any) {
    if (e?.response?.status === 401) {
      handleUnauthorized(dispatch);
      return rejectWithValue('Could not validate credentials');
    }
    return rejectWithValue(e.response?.data?.detail || 'Failed to delete session');
  }
});

// Fetch messages for a session.
//
// This thunk fires on user-initiated session switches (sidebar click,
// initial load of a session that isn't yet in memory). It used to also
// be called 2 seconds after a streaming response landed in order to
// swap temporary client UUIDs for real DB UUIDs — that path has been
// removed. The BE now returns the real bot-message id in the SSE
// 'complete' event, so the FE adopts it inline without a follow-up
// fetch (see updateMessageId reducer).
//
// 401 handling is intentionally silent here. The thunk is sometimes
// called in background (lazy-load on session switch) and a forced
// /login redirect from a background path produced the worst kind of
// UX bug — user gets their answer, two seconds later they're on the
// login page. Real interactive auth failures are caught by the global
// axios response interceptor and by sendStreamingMessage's own 401
// handling, both of which redirect explicitly.
export const fetchSessionMessages = createAsyncThunk('chat/fetchSessionMessages', async (sessionId: string, { getState, rejectWithValue }) => {
  try {
    const { auth } = getState() as { auth: { token: string } };
    const res = await axios.get(`${backendUrl}/api/chat/sessions/${sessionId}/messages`, { headers: { Authorization: `Bearer ${auth.token}` } });
    return { sessionId, messages: res.data as ChatMessage[] };
  } catch (e: any) {
    if (e?.response?.status === 401) {
      // Silent on background path — see comment above.
      return rejectWithValue('Could not validate credentials');
    }
    return rejectWithValue(e.response?.data?.detail || 'Failed to fetch session messages');
  }
});

// Rating system actions
export const submitRating = createAsyncThunk('chat/submitRating', async ({ messageId, ratingType, ratingValue, feedbackText }: { messageId: string | number; ratingType: 'thumbs' | 'stars'; ratingValue: number; feedbackText?: string }, { getState, dispatch, rejectWithValue }) => {
  const messageIdStr = messageId.toString();
  
  // Skip API call for invalid UUID format (e.g., timestamp-based IDs)
  if (!isValidUUID(messageIdStr)) {
    return rejectWithValue('Cannot rate message with invalid ID format');
  }
  
  try {
    const { auth } = getState() as { auth: { token: string } };
    const res = await axios.post(`${backendUrl}/api/chat/messages/${messageIdStr}/ratings`, {
      rating_type: ratingType,
      rating_value: ratingValue,
      feedback_text: feedbackText
    }, { headers: { Authorization: `Bearer ${auth.token}` } });
    return { messageId: messageIdStr, rating: res.data as Rating };
  } catch (e: any) {
    if (e?.response?.status === 401) {
      handleUnauthorized(dispatch);
      return rejectWithValue('Could not validate credentials');
    }
    return rejectWithValue(e.response?.data?.detail || 'Failed to submit rating');
  }
});

export const fetchMessageRating = createAsyncThunk('chat/fetchMessageRating', async (messageId: string, { getState, dispatch, rejectWithValue }) => {
  // Skip API call for invalid UUID format (e.g., timestamp-based IDs)
  if (!isValidUUID(messageId)) {
    return { messageId, rating: null };
  }
  
  try {
    const { auth } = getState() as { auth: { token: string } };
    const res = await axios.get(`${backendUrl}/api/chat/messages/${messageId}/my-rating`, {
      headers: { Authorization: `Bearer ${auth.token}` }
    });
    return { messageId, rating: res.data as Rating };
  } catch (e: any) {
    // If no rating found (404) or invalid UUID format (500), return null rating
    if (e.response?.status === 404 || e.response?.status === 500) {
      return { messageId, rating: null };
    }
    if (e?.response?.status === 401) {
      handleUnauthorized(dispatch);
      return rejectWithValue('Could not validate credentials');
    }
    return rejectWithValue(e.response?.data?.detail || 'Failed to fetch rating');
  }
});

// New streaming message action
export const sendStreamingMessage = createAsyncThunk(
  'chat/sendStreamingMessage',
  async (
    { sessionId, message, no_context_mode = false, source }:
    { sessionId: string; message: string; no_context_mode?: boolean; source?: string },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      
      // Get the current session from state to use the correct session ID
      const state = getState() as any;
      let currentSession = state.chat.currentSession;
      let actualSessionId = currentSession?.id;
      
      // Create a new session if none exists
      if (!actualSessionId) {
        try {
          // Generate title from first 50 characters of user message
          // Handle empty/whitespace-only messages
          const sanitizedMessage = message.replace(/[\n\r]/g, ' ').trim();
          const generatedTitle = sanitizedMessage.length > 0
            ? (sanitizedMessage.length > 50 
                ? sanitizedMessage.substring(0, 50).trim() + '...' 
                : sanitizedMessage)
            : 'New Chat';
          
          const newSession = await dispatch(createSession(generatedTitle)).unwrap();
          actualSessionId = newSession.id;
          // Re-fetch current session from Redux state to ensure sync
          const updatedState = getState() as any;
          currentSession = updatedState.chat.currentSession;
        } catch (error: any) {
          // Unwrap throws the rejected value (string) from createSession; don't mask it.
          const msg =
            typeof error === 'string'
              ? error
              : (error?.message || 'Failed to create new chat session.');
          return rejectWithValue(msg);
        }
      }
      
      if (!auth?.token) {
        return rejectWithValue('Authentication required. Please log in to continue.');
      }
      
      // Add user message to current session immediately with proper sequence number
      const userSeq = getNextSequenceNumber(currentSession?.messages, 'user');
      const userMessage: ChatMessage = {
        id: generateUUID(),
        session_id: actualSessionId,
        sender: 'user',
        content: message,
        created_at: new Date().toISOString(),
        sequence_number: userSeq
      };
      dispatch(addMessage(userMessage));

      // Bubble the active session to the top of the sidebar IMMEDIATELY
      // (with the user's message as preview) so the user sees instant
      // feedback instead of waiting for the bot to finish streaming.
      // The legacy chat AND early v2 both deferred this until after
      // stream — produced a "sidebar lags" UX.
      dispatch(touchSession({
        sessionId: actualSessionId,
        preview: message,
        updatedAt: new Date().toISOString(),
      }));
      
      // Create bot message placeholder immediately so the UI shows a bot "slot" right away.
      const botSeq = getNextSequenceNumber([...((currentSession?.messages || []) as ChatMessage[]), userMessage], 'bot');
      const botMessage: ChatMessage = {
        id: generateUUID(), // Temporary ID
        session_id: actualSessionId,
        sender: 'bot',
        content: '',
        created_at: new Date().toISOString(),
        sequence_number: botSeq
      };
      
      dispatch(addMessage(botMessage));

      let currentBotMessageId: string = botMessage.id;
      let combinedStreamContent = ""; // accumulate all chunks for debugging

      // ── Streaming chunk throttle (typewriter effect) ─────────────────────
      // The upstream ANIS service delivers chunks in bursts: ~280 chunks
      // can land within ~70ms once the LLM finishes generating. React 18
      // batches all those dispatches into a single commit, so without
      // throttling the user sees text "snap in" all at once instead of
      // streaming progressively. We buffer incoming chunks and drain them
      // on a ~25ms tick — slow enough that each render is perceptible,
      // adaptive enough that long backlogs catch up without feeling sluggish.
      const TYPEWRITER_TICK_MS = 25;
      let pendingStreamText = '';
      let drainTimer: ReturnType<typeof setTimeout> | null = null;

      const drainTick = () => {
        if (!pendingStreamText) {
          drainTimer = null;
          return;
        }
        // Adaptive drain: take more characters per tick when the backlog
        // is large so a 600-chunk burst doesn't take 15 seconds to render.
        // Aim to clear the backlog over roughly the same wall-clock time
        // it takes the upstream to send the SLOW portion of the stream.
        const len = pendingStreamText.length;
        const drainCount = Math.max(1, Math.min(Math.ceil(len / 10), 12));
        const piece = pendingStreamText.slice(0, drainCount);
        pendingStreamText = pendingStreamText.slice(drainCount);
        dispatch(updateMessageContent({ messageId: currentBotMessageId, content: piece }));
        drainTimer = setTimeout(drainTick, TYPEWRITER_TICK_MS);
      };

      const enqueueChunk = (text: string) => {
        if (!text) return;
        pendingStreamText += text;
        if (drainTimer === null) {
          drainTimer = setTimeout(drainTick, TYPEWRITER_TICK_MS);
        }
      };

      const flushPendingNow = () => {
        if (drainTimer !== null) {
          clearTimeout(drainTimer);
          drainTimer = null;
        }
        if (pendingStreamText) {
          dispatch(updateMessageContent({ messageId: currentBotMessageId, content: pendingStreamText }));
          pendingStreamText = '';
        }
      };

      return new Promise<ChatMessage>((resolve, reject) => {
        const abortController = new AbortController();
        activeAbortController = abortController;

        const cleanupOnError = (err: Error) => {
          activeAbortController = null;
          // Flush any chunks still queued in the typewriter buffer so the
          // partial response (or the error message we append below) lands
          // immediately rather than ticking out over the next second.
          flushPendingNow();
          try {
            // User-initiated abort: append a small italic "(stopped)"
            // hint so the user can tell the response was cut short
            // intentionally and isn't just frozen. Without this, the
            // bubble silently froze mid-sentence with no indicator —
            // people couldn't tell whether they aborted, the network
            // dropped, or the page was hung.
            if (err.name === 'AbortError') {
              const hadContent = combinedStreamContent.trim().length > 0;
              const stoppedHint = hadContent ? '\n\n_(stopped)_' : '_(stopped)_';
              dispatch(updateMessageContent({ messageId: currentBotMessageId, content: stoppedHint }));
              resolve(botMessage);
              return;
            }
            // Keep the user's message in the UI; update the bot placeholder with a visible error.
            const errMsg = err?.message || 'No response received. Please try again.';
            const prefix = combinedStreamContent.trim().length > 0 ? '\n\n' : '';
            dispatch(updateMessageContent({ messageId: currentBotMessageId, content: `${prefix}⚠️ ${errMsg}` }));
          } catch {}
          reject(err);
        };
        const timeoutId = setTimeout(() => {
          cleanupOnError(new Error('Request timeout. The chatbot is taking too long to respond. Please try again.'));
        }, 120000); // 120 second timeout

        fetch(`${backendUrl}/api/chat/chat_stream`, {
          method: 'POST',
          signal: abortController.signal,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.token}`,
            'Accept': 'text/event-stream'
          },
          body: JSON.stringify({
            session_id: actualSessionId,
            message: message,
            no_context_mode: no_context_mode,
            source: source || (getState() as any).chat.selectedSource || 'starch',
            mode: (getState() as any).chat.chatMode || 'expert',
            // Backend will use configured credentials from settings
          })
        })
        .then(response => {
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            if (response.status === 401) {
              // Keep behavior consistent with the rest of the app:
              // clear token + redirect to login with a message.
              try {
                sessionStorage.setItem(
                  'sessionExpiredMessage',
                  'Your session is no longer valid. Please log in again to continue.'
                );
              } catch {}
              dispatch(logout());
              setTimeout(() => {
                window.location.href = '/login';
              }, 100);
              throw new Error('Could not validate credentials');
            } else if (response.status === 403) {
              throw new Error('Access denied. You do not have permission to use the chatbot.');
            } else if (response.status === 404) {
              throw new Error('Chat service not found. Please contact support.');
            } else if (response.status === 429) {
              throw new Error('Too many requests. Please wait a moment before trying again.');
            } else if (response.status >= 500) {
              throw new Error('Server error. The chatbot service is temporarily unavailable. Please try again later.');
            } else {
              throw new Error(`Connection failed (${response.status}). Please check your internet connection and try again.`);
            }
          }
          
          const reader = response.body?.getReader();
          const decoder = new TextDecoder();
          
          if (!reader) {
            cleanupOnError(new Error('Failed to establish connection with chatbot. Please try again.'));
            return; // stop further processing
          }
          
          let streamTimeout: NodeJS.Timeout;
          const resetStreamTimeout = () => {
            clearTimeout(streamTimeout);
            streamTimeout = setTimeout(() => {
              reader.cancel();
              cleanupOnError(new Error('Chatbot stopped responding. The connection was interrupted. Please try again.'));
            }, 90000); // 90 second timeout for stream chunks (allows for long processing)
          };
          
          resetStreamTimeout();
          
          // Buffer to handle SSE lines split across network chunks
          let sseBuffer = '';

          function readStream(): Promise<void> {
            return reader!.read().then(({ done, value }) => {
              if (done) {
                clearTimeout(streamTimeout);
                activeAbortController = null;
                // Drain whatever's left in the typewriter buffer immediately
                // so the user doesn't see the cursor keep ticking after the
                // stream itself has ended.
                flushPendingNow();
                resolve(botMessage);
                return;
              }
              
              resetStreamTimeout(); // Reset timeout on each chunk
              
              const chunk = decoder.decode(value, { stream: true });
              sseBuffer += chunk;

              // Process complete lines; keep last partial line in buffer
              const lines = sseBuffer.split('\n');
              sseBuffer = lines.pop() ?? '';
              
              for (const rawLine of lines) {
                const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
                if (line.startsWith('data:')) {
                  try {
                    // SSE "data:" can optionally be followed by a single space.
                    const payload = line.startsWith('data: ') ? line.slice(6) : line.slice(5);
                    const data = JSON.parse(payload);
                    if (data.type === 'chunk') {
                      // Add validation to ensure data.chunk exists and is a string
                      if (data.chunk && typeof data.chunk === 'string') {
                        // Parse chunk if it's JSON-stringified (double-encoded)
                        let chunkContent = data.chunk;
                        try {
                          // Check if the chunk is a JSON-encoded string (starts and ends with quotes)
                          if (chunkContent.startsWith('"') && chunkContent.endsWith('"')) {
                            chunkContent = JSON.parse(chunkContent);
                          }
                        } catch (e) {
                          // If parsing fails, use the original chunk
                          console.warn('Could not parse chunk as JSON, using raw value:', e);
                        }

                        // Enqueue rather than dispatch directly. The upstream
                        // bursts hundreds of chunks in <100ms which React then
                        // batches into a single render — the typewriter queue
                        // spreads them across paint frames so the user sees
                        // progressive streaming instead of an all-at-once dump.
                        enqueueChunk(chunkContent);
                        // Accumulate combined content for debug output
                        combinedStreamContent += chunkContent;
                      } else {
                        console.warn('Invalid chunk data:', data);
                      }
                    } else if (data.type === 'error') {
                      const errMsg =
                        typeof data.message === 'string' && data.message.trim().length > 0
                          ? data.message
                          : 'The chatbot returned an error. Please try again.';
                      // Show error in bot bubble only (not in Alert banner).
                      // Resolve instead of reject to avoid double error display.
                      activeAbortController = null;
                      // Drain queued content first so the partial response is
                      // visible above the error message.
                      flushPendingNow();
                      dispatch(updateMessageContent({ messageId: currentBotMessageId, content: `⚠️ ${errMsg}` }));
                      resolve(botMessage);
                      return Promise.resolve();
                    } else if (data.type === 'complete') {
                      // Drain remaining typewriter content before applying
                      // metadata, so the source cards appear AFTER the text
                      // is fully rendered rather than mid-stream.
                      flushPendingNow();
                      // Record the BE-assigned DB id on the message so
                      // rating clicks immediately after streaming hit a
                      // real row instead of a temp UUID. The reducer
                      // stores it as `serverMessageId` (NOT as `id`) —
                      // mutating `id` mid-stream forks the assistant-ui
                      // MessageRepository node and orphans the rating
                      // data part on the old branch (see reducer comment).
                      // Keep `currentBotMessageId` at the client UUID so
                      // subsequent dispatches (metadata, orphan steps)
                      // still find the message by its unchanged `id`.
                      if (typeof data.message_id === 'string' && data.message_id) {
                        dispatch(updateMessageId({
                          oldId: currentBotMessageId,
                          newId: data.message_id,
                        }));
                      }
                      if (data.metadata) {
                        dispatch(updateMessageMetadata({
                          messageId: currentBotMessageId,
                          metadata: data.metadata
                        }));
                      }
                      // Auto-close any step still in "running" state so the
                      // UI doesn't hang on a spinner if upstream dropped
                      // the matching step.end (spec §3.4).
                      dispatch(closeOrphanSteps({ messageId: currentBotMessageId }));
                      // Resolve the streaming promise NOW. `complete` is the
                      // contract-level end-of-stream signal — waiting for
                      // `reader.read()` to return done=true blocks until the
                      // BE actually closes the connection, which in some
                      // staging scenarios takes longer than the 90s stream
                      // idle timeout (or never happens), leaving isRunning
                      // stuck true and blocking aui-rating emission.
                      clearTimeout(streamTimeout);
                      activeAbortController = null;
                      resolve(botMessage);
                      return Promise.resolve();
                    } else if (data.type === 'step.start') {
                      // Intermediate step lifecycle events — render live in
                      // the <MessageSteps /> timeline above the bubble.
                      dispatch(startMessageStep({
                        messageId: currentBotMessageId,
                        step: {
                          id: data.id,
                          kind: data.kind,
                          label: data.label,
                          detail: data.detail ?? null,
                          started_at: data.started_at ?? new Date().toISOString(),
                          status: 'running',
                        },
                      }));
                    } else if (data.type === 'step.update') {
                      dispatch(updateMessageStep({
                        messageId: currentBotMessageId,
                        id: data.id,
                        detail: data.detail ?? '',
                      }));
                    } else if (data.type === 'step.end') {
                      dispatch(endMessageStep({
                        messageId: currentBotMessageId,
                        id: data.id,
                        status: (data.status === 'error' ? 'error' : 'ok'),
                        duration_ms: data.duration_ms ?? null,
                        result_preview: data.result_preview ?? null,
                        error_message: data.error_message ?? null,
                      }));
                    } else if (data.type === 'heartbeat') {

                    }
                  } catch (e) {
                    // Log parsing errors instead of silently ignoring
                    console.error('Failed to parse SSE data:', line, e);
                  }
                }
              }
              
              return readStream();
            }).catch(streamError => {
              clearTimeout(streamTimeout);
              if (streamError.name === 'AbortError') {
                cleanupOnError(new Error('Connection was interrupted. Please try again.'));
              } else {
                cleanupOnError(new Error('Network error while receiving response. Please check your connection and try again.'));
              }
            });
          }
          
          return readStream();
        })
        .catch(error => {
          clearTimeout(timeoutId);
          if (error.name === 'TypeError' && (error.message || '').includes('fetch')) {
            cleanupOnError(new Error('Unable to connect to chatbot service. Please check your internet connection and try again.'));
          } else if (error.name === 'AbortError') {
            cleanupOnError(new Error('Request was cancelled. Please try again.'));
          } else if (error instanceof Error) {
            cleanupOnError(error);
          } else {
            cleanupOnError(new Error('Unknown error occurred while connecting to chatbot.'));
          }
        });
      });
      
    } catch (e: any) {
      console.error('Streaming message error:', e);
      if (e.message) {
        return rejectWithValue(e.message);
      } else if (e.name === 'NetworkError') {
        return rejectWithValue('Network connection failed. Please check your internet connection and try again.');
      } else {
        return rejectWithValue('An unexpected error occurred. Please try again or contact support if the problem persists.');
      }
    }
  }
);

/**
 * Keep `state.currentSession` and the matching slot in
 * `state.sessions` pointing at the same object after a mutation.
 *
 * Why this exists — REGRESSION HISTORY:
 *
 * `createSession.fulfilled` and `setCurrentSession` set
 * `state.currentSession` to the SAME object reference as
 * `state.sessions[idx]` (where idx is the matching session). Reducers
 * that then mutate `state.currentSession.messages` (addMessage,
 * updateMessageContent, updateMessageId, etc.) cause Immer to produce
 * a NEW immutable copy on the `state.currentSession` path — but
 * `state.sessions[idx]` still points at the OLD pre-mutation copy
 * with the OLD (empty) messages array.
 *
 * Symptom on prod: user sends a message, watches the streamed
 * response, then clicks anywhere else and back to the same session
 * in the sidebar. `setCurrentSession` looks up
 * `state.sessions[idx]` (which has the stale empty messages) and
 * assigns it back to `state.currentSession`. The streamed content
 * vanishes from view even though it's safe in the database — the
 * chat panel falls back to the welcome screen because
 * `currentSession.messages.length === 0`.
 *
 * Fix: every reducer that mutates `state.currentSession.messages`
 * (or any field on `state.currentSession`) MUST end by re-pointing
 * `state.sessions[idx]` at the new `state.currentSession`. Doing so
 * via Immer assignment keeps both paths referencing the same object,
 * so a later `setCurrentSession` finds the up-to-date data.
 *
 * `fetchSessionMessages.fulfilled` already updates BOTH paths
 * explicitly — this helper consolidates the same discipline for the
 * many small mutating reducers.
 */
function syncCurrentSessionToList(state: ChatState) {
  if (!state.currentSession) return;
  const idx = state.sessions.findIndex(s => s.id === state.currentSession!.id);
  if (idx === -1) return;
  state.sessions[idx] = state.currentSession;
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    setCurrentSession(state, action) {
      const sessionId = action.payload;
      if (sessionId === null) {
        state.currentSession = null;
        saveCurrentSessionId(null);
      } else {
        state.currentSession = state.sessions.find(s => s.id === sessionId) || null;
        saveCurrentSessionId(sessionId);
      }
    },
    addMessage(state, action) {
      const message = action.payload as ChatMessage;
      if (state.currentSession && state.currentSession.id === message.session_id) {
        // Add sequence number based on current message count to maintain order
        if (!message.sequence_number) {
          message.sequence_number = state.currentSession.messages.length;
        }
        state.currentSession.messages.push(message);
        // Sort messages by sequence number to maintain consistent order
        state.currentSession.messages.sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));
        syncCurrentSessionToList(state);
      }
    },
    updateMessageContent(state, action) {
      const { messageId, content } = action.payload;
      if (state.currentSession) {
        const message = state.currentSession.messages.find(m => m.id === messageId);
        if (message) {
          // Content is now formatted by the unified backend processor
          message.content += content;
          syncCurrentSessionToList(state);
        }
      }
    },
    /**
     * Record the BE-assigned canonical ID on the message. Dispatched
     * on the SSE 'complete' event. Stores the real id on a separate
     * `serverMessageId` field instead of mutating `id` — mid-stream
     * id swaps fork the assistant-ui MessageRepository node (id is
     * the lookup key), creating a sibling branch and stranding the
     * post-stream rating data part on an orphaned branch.
     */
    updateMessageId(state, action: { payload: { oldId: string; newId: string } }) {
      const { oldId, newId } = action.payload;
      if (!oldId || !newId || oldId === newId) return;
      if (state.currentSession) {
        const message = state.currentSession.messages.find(m => m.id === oldId);
        if (message) {
          message.serverMessageId = newId;
          syncCurrentSessionToList(state);
        }
      }
    },
    parseMessageSources(state, action) {
      const { messageId } = action.payload;
      if (state.currentSession) {
        const message = state.currentSession.messages.find(m => m.id === messageId);
        if (message && message.sender === 'bot') {
          const parsed = parseMessageSourcesUtil(message.content);
          message.content = parsed.content;
          message.sources = parsed.sources;
          syncCurrentSessionToList(state);
        }
      }
    },
    updateMessageMetadata(state, action) {
      const { messageId, metadata } = action.payload;
      if (state.currentSession) {
        const message = state.currentSession.messages.find(m => m.id === messageId);
        if (message) {
          const existingSteps = message.metadata?.steps;
          message.metadata = existingSteps
            ? { ...metadata, steps: existingSteps }
            : metadata;
          syncCurrentSessionToList(state);
        }
      }
    },
    /**
     * Intermediate step lifecycle reducers. See CHAT_INTERMEDIATE_STEPS_SPEC.
     *
     * Steps are attached to the bot message being streamed; the SSE parser
     * dispatches these as step.start / step.end arrives. The component
     * renders them into a timeline above the message bubble.
     */
    startMessageStep(state, action: { payload: { messageId: string; step: Omit<StepRecord, 'status'> & { status?: StepStatus } } }) {
      const { messageId, step } = action.payload;
      if (!state.currentSession) return;
      const msg = state.currentSession.messages.find(m => m.id === messageId);
      if (!msg) return;
      if (!msg.metadata) msg.metadata = {};
      if (!msg.metadata.steps) msg.metadata.steps = [];
      // De-dupe by id in case of retry/duplicate start events.
      if (msg.metadata.steps.some(s => s.id === step.id)) return;
      msg.metadata.steps.push({
        ...step,
        status: step.status ?? 'running',
      });
      syncCurrentSessionToList(state);
    },

    updateMessageStep(state, action: { payload: { messageId: string; id: string; detail: string } }) {
      const { messageId, id, detail } = action.payload;
      if (!detail) return;
      if (!state.currentSession) return;
      const msg = state.currentSession.messages.find(m => m.id === messageId);
      if (!msg?.metadata?.steps) return;
      const step = msg.metadata.steps.find(s => s.id === id);
      if (!step) return;
      step.detail = (step.detail || '') + detail;
      syncCurrentSessionToList(state);
    },
    endMessageStep(state, action: { payload: { messageId: string; id: string; status: StepStatus; duration_ms?: number | null; result_preview?: string | null; error_message?: string | null; ended_at?: string } }) {
      const { messageId, id, status, duration_ms, result_preview, error_message, ended_at } = action.payload;
      if (!state.currentSession) return;
      const msg = state.currentSession.messages.find(m => m.id === messageId);
      if (!msg?.metadata?.steps) return;
      const step = msg.metadata.steps.find(s => s.id === id);
      if (!step) return;
      step.status = status;
      step.duration_ms = duration_ms ?? step.duration_ms ?? null;
      step.result_preview = result_preview ?? step.result_preview ?? null;
      step.error_message = error_message ?? step.error_message ?? null;
      step.ended_at = ended_at ?? new Date().toISOString();
      syncCurrentSessionToList(state);
    },
    /**
     * Called on stream completion — auto-closes any step still in "running"
     * state as an error (per §3.4 of the spec). Prevents stuck spinners.
     */
    closeOrphanSteps(state, action: { payload: { messageId: string } }) {
      const { messageId } = action.payload;
      if (!state.currentSession) return;
      const msg = state.currentSession.messages.find(m => m.id === messageId);
      if (!msg?.metadata?.steps) return;
      const now = new Date().toISOString();
      for (const step of msg.metadata.steps) {
        if (step.status === 'running') {
          step.status = 'error';
          step.error_message = step.error_message ?? 'Stream ended before step completed';
          step.ended_at = now;
        }
      }
      syncCurrentSessionToList(state);
    },
    removeMessage(state, action) {
      const messageId: string = action.payload;
      if (state.currentSession) {
        state.currentSession.messages = state.currentSession.messages.filter(m => m.id !== messageId);
        syncCurrentSessionToList(state);
      }
    },
    /**
     * Update the sidebar row for a single session in place after a message
     * is sent — preview + timestamp + re-sort. Replaces the full-list
     * `fetchSessions()` refetch that used to fire on every send and caused
     * the whole sidebar to flicker / re-key.
     *
     * Safe to call with a sessionId that isn't in state; it's a no-op then.
     */
    touchSession(state, action: { payload: { sessionId: string; preview?: string | null; updatedAt?: string } }) {
      const { sessionId, preview, updatedAt } = action.payload;
      if (!sessionId) return;
      const idx = state.sessions.findIndex(s => s.id === sessionId);
      if (idx === -1) return;
      const ts = updatedAt || new Date().toISOString();
      const existing = state.sessions[idx];
      // Mutate fields in place so references to other session objects stay stable.
      existing.last_message_preview = preview ?? existing.last_message_preview ?? null;
      existing.last_message_at = ts;
      // Bubble to top (most-recent-first order the sidebar expects).
      if (idx !== 0) {
        state.sessions.splice(idx, 1);
        state.sessions.unshift(existing);
      }
      if (state.currentSession && state.currentSession.id === sessionId) {
        state.currentSession.last_message_preview = existing.last_message_preview;
        state.currentSession.last_message_at = existing.last_message_at;
      }
    },
    setSelectedSource(state, action) {
      state.selectedSource = action.payload;
    },
    setChatMode(state, action: { payload: ChatMode }) {
      state.chatMode = action.payload;
      try {
        localStorage.setItem('aihub_chat_mode', action.payload);
      } catch {}
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSessions.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchSessions.fulfilled, (s, a) => {
        s.loading = false;
        // Lightweight session payload — messages are lazy-loaded per session.
        // Preserve any messages already loaded for sessions the user has opened.
        const existingMessagesById: Record<string, { messages: ChatMessage[]; messagesLoaded?: boolean }> = {};
        for (const existing of s.sessions) {
          if (existing.messagesLoaded) {
            existingMessagesById[existing.id] = {
              messages: existing.messages,
              messagesLoaded: true,
            };
          }
        }
        s.sessions = a.payload.map((session: any) => {
          const cached = existingMessagesById[session.id];
          return {
            ...session,
            messages: cached ? cached.messages : [],
            messagesLoaded: !!cached?.messagesLoaded,
          };
        });
        /* MODIFIED: Always start with new session instead of loading saved session */
        if (!s.currentSession) {
          s.currentSession = null; /* Will trigger new session creation in component */
        }
      })
      .addCase(fetchSessions.rejected, (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(createSession.fulfilled, (s, a) => {
        // A session created via the API has zero messages by definition —
        // mark messagesLoaded:true so the lazy-load useEffect in
        // Chatbot.tsx doesn't fire fetchSessionMessages on it. Without
        // this flag, fetchSessionMessages fires immediately after
        // createSession, races with sendStreamingMessage's addMessage
        // dispatches, and eventually OVERWRITES currentSession.messages
        // with the empty backend response — wiping the user message and
        // the bot placeholder that streaming dispatches target. Symptom
        // user reported: clicking a suggestion card "doesn't initiate
        // streaming" because the bot message vanishes mid-stream so
        // updateMessageContent finds nothing to append to.
        const seed = { ...a.payload, messages: [], messagesLoaded: true };
        s.sessions.unshift(seed);
        s.currentSession = seed;
        saveCurrentSessionId(a.payload.id);
      })
      .addCase(sendMessage.fulfilled, (s, a) => { if (s.currentSession && s.currentSession.id === a.payload.session_id) { s.currentSession.messages.push(a.payload); } })
      .addCase(sendStreamingMessage.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(sendStreamingMessage.fulfilled, (s) => { s.loading = false; })
      .addCase(sendStreamingMessage.rejected, (s, a) => {
        s.loading = false;
        // When the thunk throws (promise rejection), payload can be undefined; preserve message via a.error.message.
        s.error = (a.payload as string) || a.error?.message || 'Failed to send message. Please try again.';
      })
      // Session management reducers
      .addCase(updateSession.fulfilled, (s, a) => {
        const sessionIndex = s.sessions.findIndex(session => session.id === a.payload.id);
        if (sessionIndex !== -1) {
          s.sessions[sessionIndex] = { ...s.sessions[sessionIndex], ...a.payload };
          if (s.currentSession && s.currentSession.id === a.payload.id) {
            s.currentSession = { ...s.currentSession, ...a.payload };
          }
        }
      })
      .addCase(deleteSession.fulfilled, (s, a) => {
        s.sessions = s.sessions.filter(session => session.id !== a.payload);
        if (s.currentSession && s.currentSession.id === a.payload) {
          s.currentSession = s.sessions.length > 0 ? s.sessions[0] : null;
        }
      })
      // Update current session with real message IDs from backend
      .addCase(fetchSessionMessages.fulfilled, (s, a) => {
        const { sessionId, messages } = a.payload;

        // Ensure proper ordering - sources are now handled by backend
        const processedMessages = messages
          .sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0))
          .map(message => {
            // Backend now handles all formatting via unified processor
            // Map message_metadata to metadata for frontend compatibility
            const { message_metadata, ...rest } = message as any;
            // Backend persists the SSE step stream as raw start/end
            // events; reconstruct the canonical merged StepRecord shape
            // before it hits any renderer. See mergePersistedSteps.ts.
            const metadata = message_metadata
              ? {
                  ...message_metadata,
                  ...(message_metadata.steps
                    ? { steps: mergePersistedSteps(message_metadata.steps) }
                    : {}),
                }
              : message_metadata;
            return {
              ...rest,
              metadata,
            };
          });

        if (s.currentSession && s.currentSession.id === sessionId) {
          // Update current session messages with real backend IDs and parsed sources
          s.currentSession.messages = processedMessages;
          s.currentSession.messagesLoaded = true;
        }
        // Also update in sessions array
        const sessionIndex = s.sessions.findIndex(session => session.id === sessionId);
        if (sessionIndex !== -1) {
          s.sessions[sessionIndex].messages = processedMessages;
          s.sessions[sessionIndex].messagesLoaded = true;
        }
      })
      // Rating system reducers
      .addCase(submitRating.fulfilled, (s, a) => {
        // Store the submitted rating in state
        s.messageRatings[a.payload.messageId] = a.payload.rating;
      })
      .addCase(fetchMessageRating.fulfilled, (s, a) => {
        // Store the fetched rating in state (or null if no rating exists)
        if (a.payload.rating) {
          s.messageRatings[a.payload.messageId] = a.payload.rating;
        }
      });
  }
});

export const { clearError, setCurrentSession, addMessage, updateMessageContent, updateMessageId, parseMessageSources, updateMessageMetadata, removeMessage, setSelectedSource, setChatMode, touchSession, startMessageStep, updateMessageStep, endMessageStep, closeOrphanSteps } = chatSlice.actions;
export default chatSlice.reducer;


